import { gql, useMutation } from "@apollo/client";
import * as React from "react";

import { convertStatusValueToFilter } from "@zapier/reporting/src/constants";
import {
  RunFilter,
  ZapHistoryExportFormat,
} from "@zapier/reporting/src/graphql-types";
import { HistoryFiltersTypes } from "@zapier/reporting/src/types";
import { getErrorMessage } from "@zapier/reporting/src/utils";
import { DownloadRunsMutation } from "./useDownloadRuns.generated";

export enum DownloadRunsActions {
  INITIAL,
  SENDING,
  SENT_SUCCESS,
  SENT_FAILURE,
}

type Input = {
  filters: Partial<HistoryFiltersTypes>;
};

const downloadRunsMutation = gql`
  mutation DownloadRuns(
    $filters: RunFilter!
    $format: ZapHistoryExportFormat
    $excludeChildren: Boolean
    $runIds: [ID!]
  ) {
    downloadRuns(
      filters: $filters
      format: $format
      excludeChildren: $excludeChildren
      runIds: $runIds
    ) {
      message
    }
  }
`;

export function useDownloadRuns(input?: Input) {
  const [downloadRuns, downloadRunsResult] =
    useMutation<DownloadRunsMutation>(downloadRunsMutation);

  const onDownload = React.useCallback(
    (
      runIds: string[],
      isAllSelected: boolean,
      totalActionable: number,
      format = ZapHistoryExportFormat.JSON,
      // Defaults to including everything: a caller shouldn't silently lose
      // step data just because it didn't state a preference.
      excludeChildren: boolean = false
    ) => {
      const limit =
        isAllSelected && totalActionable ? totalActionable : undefined;

      const filters: RunFilter = {
        apps: input?.filters.apps,
        customuserIds: input?.filters.ownerIds,
        folderIds: input?.filters.folderIds,
        limit,
        periodEnd: input?.filters.dateFilter?.to,
        periodStart: input?.filters.dateFilter?.from,
        query: input?.filters.search,
        status: convertStatusValueToFilter(input?.filters.status),
        zapIds: input?.filters.nodeIds,
      };

      return downloadRuns({
        variables: {
          filters,
          excludeChildren,
          format,
          runIds: isAllSelected ? null : runIds,
        },
      });
    },
    [downloadRuns, input?.filters]
  );

  const error = getErrorMessage(downloadRunsResult.error);

  return {
    error,
    isLoading: downloadRunsResult.loading,
    onDownload,
  };
}
